import React, { useContext, useEffect, useState } from 'react';

import profileReadyImage from 'images/illustrations/profile-ready.webp';
import {
  Onboarding,
  OnboardingContext,
  OnboardingSubtitle as OnboardingSubtitleBase,
  OnboardingTitle,
} from 'registration/containers/Onboarding';

import { Drawer, PrimaryButton, SIZE } from 'shared/components/ihcl/drawer';
import {
  FirstAidIcon,
  HospitalBedIcon,
  HospitalHouseIcon,
  HospitalIcon,
  HospitalPhoneIcon,
  NurseIcon,
} from 'shared/components/ihcl/icon';
import { Center } from 'shared/components/ihcl/positioning';
import { styled, withStyle } from 'shared/components/ihcl/styled';
import { ButtonSelect } from 'shared/components/ihcl/v2/button-select';
import simplePluralize from 'shared/helpers/simplePluralize';

const OnboardingSubtitle = withStyle(OnboardingSubtitleBase, {
  marginBottom: '32px',
});

const IconWrapper = styled('div', {
  marginRight: '8px',
});

export const HeaderImage = styled('img', ({ $theme }) => ({
  maxWidth: `min(calc(100vw - ${$theme.sizing.unit96}), 500px - ${$theme.sizing.unit96})`,
  maxHeight: 'calc(50vh - 150px)',
}));

export const DrawerContents = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const getCareSettingIcon = (careSetting) => {
  switch (careSetting.short_name) {
    case 'acute_care': {
      return <HospitalIcon />;
    }
    case 'ambulatory': {
      return <FirstAidIcon />;
    }
    case 'home_health': {
      return <HospitalHouseIcon />;
    }
    case 'long_term_acute_care': {
      return <HospitalBedIcon />;
    }
    case 'skilled_nursing': {
      return <NurseIcon />;
    }
    case 'telehealth': {
      return <HospitalPhoneIcon />;
    }
    default: {
      return null;
    }
  }
};

const augmentCareSettings = (careSettings) => {
  const augmentedCareSettings = careSettings.map((careSetting) => ({
    ...careSetting,
    label: (
      <>
        <IconWrapper>{getCareSettingIcon(careSetting)}</IconWrapper>
        {careSetting.label}
      </>
    ),
  }));
  return augmentedCareSettings;
};

type OnboardingCareSettingsProps = {
  nextAction?: string;
  previousAction?: string;
  stepNumber: number;
};
const OnboardingCareSettings = ({
  nextAction = null,
  previousAction = null,
  stepNumber,
}: OnboardingCareSettingsProps) => {
  // FIXME: Context should work without casting to any
  const {
    isLiteProfileActivating,
    onboardingData,
    onboardingOptions,
    setOnboardingData,
  }: any = useContext(OnboardingContext);
  const [seenResumeUploadDrawer, setSeenResumeUploadDrawer] = useState(false);

  useEffect(() => {
    if (isLiteProfileActivating && !onboardingData.hiring_activated) {
      setOnboardingData((data) => ({ ...data, hiring_activated: true }));
    }
  }, [
    isLiteProfileActivating,
    onboardingData.hiring_activated,
    setOnboardingData,
  ]);

  const { care_settings: careSettingsOrig } = onboardingOptions;
  const careSettings = augmentCareSettings(careSettingsOrig);
  const { care_settings: selectedCareSettings } = onboardingData;
  if (!onboardingData || Object.keys(onboardingData).length < 1) {
    return null;
  }

  return (
    <Onboarding
      currentStep={stepNumber}
      isNextDisabled={!selectedCareSettings || selectedCareSettings.length < 1}
      nextAction={nextAction}
      previousAction={previousAction}
      stepKeys={['care_settings', 'hiring_activated']}
      stepName="Care settings"
    >
      <Center>
        <OnboardingTitle>
          Which care settings are you open to working in?
        </OnboardingTitle>
        <OnboardingSubtitle>
          {simplePluralize(selectedCareSettings.length, 'setting', 'settings')}{' '}
          selected
        </OnboardingSubtitle>
        <ButtonSelect
          className="flex-col items-center"
          options={careSettings}
          values={selectedCareSettings.map((careSetting) => careSetting.value)}
          onValuesChange={(next) =>
            setOnboardingData({
              ...onboardingData,
              care_settings: careSettings.filter((careSetting) =>
                next.includes(careSetting.value)
              ),
            })
          }
        />
      </Center>
      <Drawer
        isOpen={
          !seenResumeUploadDrawer &&
          onboardingData.account_info?.email_in_use === false
        }
        size={SIZE.default}
        onClose={() => setSeenResumeUploadDrawer(true)}
        actions={[
          <PrimaryButton
            key="got-it-button"
            onClick={() => {
              setSeenResumeUploadDrawer(true);
            }}
          >
            Got it
          </PrimaryButton>,
        ]}
      >
        <DrawerContents>
          <HeaderImage src={profileReadyImage} alt="Freshly baked pie" />
          <div>
            <OnboardingSubtitleBase>
              <strong>Your resume has been analyzed</strong>
            </OnboardingSubtitleBase>
            <OnboardingSubtitleBase $noTopMargin>
              Please review and confirm the following information, and we’ll get
              your job matches.
            </OnboardingSubtitleBase>
          </div>
        </DrawerContents>
      </Drawer>
    </Onboarding>
  );
};

export default OnboardingCareSettings;
